import React, { useEffect, useMemo, useState } from "react";
import * as Mui from "@mui/material";
import * as Icons from "react-feather";
import { useSelector } from "react-redux";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import { DescribedRadioGroup } from "component/shared/described-radio-group";
import { DialogFrame } from "component/shared/dialog-frame";
import { assignSteps, MAKE_BILLABLE_FAILED, useFixRun, useMakeBillable } from "component/shared/mls-id-dialogs";
import { OutlinedButton } from "component/shared/outlined-button";
import { getUser } from "redux/selector";
import * as paths from "routes/paths";
import { AgentMlsIds } from "model/agent-mls-ids";
import { REVIEW_SAVE_LABEL, ReviewChoice, reviewDone, reviewNote, reviewPlan } from "./review-plan";
import { styles } from "./style";

interface Props extends WithStyles<typeof styles> {
	agentMlsIds: AgentMlsIds;
	// The Unknown, Not Billable or Shared row; null when closed.
	clientBoardId: number | null;
	// The toast once the change lands, or a warning when Assign stopped partway.
	onSaved: (message: string, severity?: "warning") => void;
	onClose: () => void;
}

// Review on an Unknown, Not Billable or Shared row: assign it to a billable lead owner, make its agent billable,
// or delete it from Featured Listings. Assign and delete run as fix steps (the shared dialogs' fix-steps.ts).
const Component: React.FC<Props> = ({ agentMlsIds, clientBoardId, onSaved, onClose, classes = {} }) => {
	// The admin check the Access tab uses for its Lead Owner control: only an admin is offered Make Billable.
	const admin = !!useSelector(getUser)?.admin;
	const plan = useMemo(() => (clientBoardId != null ? reviewPlan(agentMlsIds, clientBoardId, admin) : null), [agentMlsIds, clientBoardId, admin]);
	const [choice, setChoice] = useState<ReviewChoice>("delete");
	const [agentId, setAgentId] = useState<number | null>(null);
	// Make Billable is an agent save, not a fix run, so it keeps its own saving and error.
	const [billing, setBilling] = useState(false);
	const [billError, setBillError] = useState<string | null>(null);
	const [pickError, setPickError] = useState<string | null>(null);
	const makeBillable = useMakeBillable(plan?.billAgent?.agentId ?? null);
	const fixRun = useFixRun(agentMlsIds);
	const saving = billing || fixRun.saving;
	const error = billError ?? fixRun.error;

	useEffect(() => {
		setChoice(plan ? plan.options[0].choice : "delete");
		setAgentId(null);
		setBilling(false);
		setBillError(null);
		fixRun.reset();
		setPickError(null);
	}, [clientBoardId]);

	if (!plan) {
		return null;
	}
	const note = reviewNote(plan, choice, agentId);
	const pick = (next: ReviewChoice) => {
		setChoice(next);
		setBillError(null);
		fixRun.setError(null);
		setPickError(null);
	};

	const save = async () => {
		if (choice === "assign" && agentId === null) {
			setPickError("Select the billable lead owner this MLS ID belongs to.");
			return;
		}
		const done = (name: string) => () => {
			onSaved(reviewDone(plan, choice, name));
			onClose();
		};
		const partial = (message: string) => {
			onSaved(message, "warning");
			onClose();
		};
		const rowId = plan.clientBoardId;
		if (choice === "delete") {
			// Only while the row is still in the state the delete was offered for: a row fixed since stays on Featured.
			const deletable = (current: AgentMlsIds) => reviewPlan(current, rowId)?.state === plan.state;
			fixRun.run({ plan: (current) => (deletable(current) ? [{ kind: "rowToNone", clientBoardId: rowId }] : null), onDone: done(""), onPartial: partial });
		} else if (choice === "assign" && agentId !== null) {
			const name = plan.leadOwners.find((o) => o.agentId === agentId)?.name ?? "";
			fixRun.run({ plan: (current) => assignSteps(current, rowId, agentId), onDone: done(name), onPartial: partial });
		} else if (choice === "bill" && plan.billAgent) {
			setBilling(true);
			setBillError(null);
			try {
				await makeBillable();
				done(plan.billAgent.name)();
			} catch(e) {
				setBillError(MAKE_BILLABLE_FAILED);
				setBilling(false);
			}
		}
	};

	// Assign's dropdown, under its option.
	const leadOwnerPicker = (
		<div className={classes.leadOwner}>
			<div className={classes.leadOwnerHead}>
				<Mui.InputLabel htmlFor="review-lead-owner" required>Billable Lead Owner</Mui.InputLabel>
				{plan.state === "unknown" && (
					<Mui.Link className={classes.addLink} href={paths.settingsAgents} target="_blank" rel="noopener">
						Add Billable Lead Owner
						<Icons.ExternalLink size={14} />
					</Mui.Link>
				)}
			</div>
			<Mui.TextField
				id="review-lead-owner"
				select
				fullWidth
				value={agentId ?? ""}
				onChange={(event) => {
					setAgentId(Number(event.target.value));
					setPickError(null);
				}}
				error={!!pickError}
				helperText={pickError}
				SelectProps={{ displayEmpty: true }}
			>
				<Mui.MenuItem value="" disabled>Select a billable lead owner</Mui.MenuItem>
				{plan.leadOwners.map((owner) => (
					<Mui.MenuItem key={owner.agentId} value={owner.agentId} disabled={!!owner.disabledReason}>
						{owner.name}{owner.disabledReason ? ` (${owner.disabledReason})` : ""}
					</Mui.MenuItem>
				))}
			</Mui.TextField>
		</div>
	);

	return (
		<DialogFrame
			open
			onClose={onClose}
			title={plan.title}
			titleId="review-mls-id-title"
			closeDisabled={saving}
			notice={error && <Mui.Alert severity="error">{error}</Mui.Alert>}
			actions={
				<>
					<OutlinedButton onClick={onClose} disabled={saving}>Cancel</OutlinedButton>
					<Mui.Button variant="contained" color="primary" onClick={save} disabled={saving}>{REVIEW_SAVE_LABEL[choice]}</Mui.Button>
				</>
			}
		>
			<Mui.Typography className={classes.prompt}>{plan.prompt}</Mui.Typography>
			<DescribedRadioGroup
				name="review-choice"
				value={choice}
				onChange={(next) => pick(next as ReviewChoice)}
				options={plan.options.map((option) => ({
					value: option.choice,
					label: option.label,
					description: option.description,
					after: option.choice === "assign" && choice === "assign" ? leadOwnerPicker : undefined,
				}))}
			/>
			{note && <Mui.Alert severity={note.severity} className={classes.note}>{note.text}</Mui.Alert>}
		</DialogFrame>
	);
};

export const ReviewDialog = withStyles(Component, styles);
